<template>
  <div
    id="dropdown"
    ref="dropdown"
    class="dropdown"
    :class="{ fluid }">
    <button
      ref="button"
      type="button"
      class="handle-button"
      :class="{ open: isOpen, error, disabled, filled: props.type === 'filled' }"
      @click="handleOpen">
      <template v-if="props.selectable">
        {{ selectedItem?.label }}
      </template>
      <slot v-if="slots.default" />

      <span
        v-if="!slots.default && selectable"
        class="action-icons">
        <span
          class="icon"
          :class="{ open: isOpen && selectable, error }">
          <BaseSvg
            :icon="props.handleIcon"
            width="10px"
            height="10px" />
        </span>
      </span>
    </button>
    <div
      v-if="error"
      class="error-container">
      <span> {{ errorText }} </span>
    </div>

    <Teleport
      v-if="isOpen"
      to="body">
      <ul
        v-show="isOpen"
        ref="dropdownList"
        :class="props.position"
        class="dropdown-items"
        :style="{ ...positionStyles }">
        <li
          v-for="option in options"
          :key="option.value"
          :class="{ selected: selectedItem?.value === option.value }"
          @click="selectOption(option)">
          <div class="option">
            <div
              v-if="option.leftIcon"
              class="left">
              <BaseSvg :src="option.leftIcon" />
            </div>
            <span :class="{ center: !option.rightIcon && !option.leftIcon }">
              {{ option.label }}</span
            >
            <div
              v-if="option.rightIcon"
              class="right">
              <BaseSvg :src="option.rightIcon" />
            </div>
          </div>
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  toRef,
  useSlots,
  useTemplateRef,
  watch
} from 'vue';
import { BaseSvg } from '@/components/Base/index';
import { useClickOutside } from '@/composables';
import { DownArrow } from '@/assets/icons';

interface DropdownOption {
  key: string | number;
  value: string;
  label: string;
  fluid?: boolean;
  selected?: boolean;
  leftIcon?: string;
  rightIcon?: string;
}

export interface Props {
  value?: string;
  error?: boolean;
  errorText?: string;
  selectLabel?: string | { label: string; value: string };
  disabled?: boolean;
  fluid?: boolean;
  handleIcon?: string;
  type?: 'filled' | 'default';
  options: Array<DropdownOption>;
  position?: 'top' | 'bottom' | 'left' | 'right';
  selectable?: boolean;
  deselectable?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  selectLabel: 'Seç',
  fluid: false,
  handleIcon: DownArrow,
  type: 'default',
  errorText: 'error',
  position: 'bottom',
  deselectable: false
});

const emit = defineEmits(['change']);
const options = toRef(props, 'options');
// const dropdownRef = useTemplateRef<HTMLDivElement>('dropdown');
const slots = useSlots();
const scrollY = ref(0);
const isOpen = ref(false);
const buttonRef = useTemplateRef<HTMLButtonElement>('button');
const dropdownListRef = useTemplateRef<HTMLUListElement>('dropdownList');
const positionStyles = reactive({
  top: '',
  left: '',
  right: '',
  bottom: '',
  minWidth: ''
});

const calculatePosition = () => {
  nextTick(() => {
    if (!buttonRef.value || !dropdownListRef.value) return;

    const buttonRect = buttonRef.value.getBoundingClientRect();
    const dropdownRect = dropdownListRef.value.getBoundingClientRect();

    const bodyRect = document.body.getBoundingClientRect();

    scrollY.value = window.scrollY;

    switch (props.position) {
      case 'bottom':
        positionStyles.top = `${buttonRect.bottom + scrollY.value + 5}px`;
        positionStyles.left = `${buttonRect.left - bodyRect.left + buttonRect.width / 2 - dropdownRect.width / 2}px`;
        break;
      case 'top':
        positionStyles.top = `${buttonRect.top + scrollY.value - dropdownRect.height - 5}px`;
        positionStyles.left = `${buttonRect.left - bodyRect.left + buttonRect.width / 2 - dropdownRect.width / 2}px`;
        break;
      case 'left':
        positionStyles.top = `${buttonRect.top + scrollY.value}px`;
        positionStyles.left = `${buttonRect.left - bodyRect.left - dropdownRect.width - 5}px`;
        break;
      case 'right':
        positionStyles.top = `${buttonRect.top + scrollY.value}px`;
        positionStyles.left = `${buttonRect.right - bodyRect.left + 5}px`;
        break;
    }
  });
};

onMounted(() => {
  window.addEventListener('resize', calculatePosition);
  window.addEventListener('scroll', calculatePosition, true);
});
onBeforeUnmount(() => {
  window.removeEventListener('resize', calculatePosition);
  window.removeEventListener('scroll', calculatePosition, true);
});
watch(
  () => isOpen.value,
  () => {
    if (isOpen.value) {
      calculatePosition();
    }
    nextTick(() => {});
  }
);

onBeforeMount(() => {
  const selectedItem = options.value.find((opt) => opt.value === props.value);
  if (selectedItem && props.selectable) {
    selectedItem.selected = true;
  }
});

useClickOutside([dropdownListRef, buttonRef], () => {
  isOpen.value = false;
});

const selectOption = (option: DropdownOption) => {
  options.value.map((item) => {
    if (!props.deselectable && item.key === option.key && props.selectable) {
      item.selected = true;
      return;
    }
    if (item.key === option.key && props.selectable) {
      item.selected = !item.selected;
    } else {
      item.selected = false;
    }
  });

  const selectItem = options.value.find((item: DropdownOption) => item.selected === true);
  if (!props.selectable) {
    emit('change', option);
  }
  if (selectItem) {
    emit('change', { selectItem, options });
  }

  isOpen.value = false;
};

const selectedItem = computed(() => {
  const selectItem = options.value.find((item: DropdownOption) => item.selected === true);
  return (
    selectItem || {
      key: 1,
      value: 'select',
      label: props.selectLabel
    }
  );
});

const handleOpen = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
};
</script>

<style lang="scss" src="./styles/dropdown.scss" scoped/>
